import { formatISO } from "date-fns";
import parseISO from "date-fns/parseISO";
import { action } from "mobx";
import { Observer } from "mobx-react";
import { Calendar, CalendarProps } from "primereact/calendar";
import React from "react";
import { GetDetailsForCandidateResponse } from "../../../api/candidate/getDetailsForCandidate";
import { getLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { invalidateQueries } from "../../../WSUtils/useLatestQuery";
import { concatClassnames, isTouchCapable } from "../../../WSUtils/util";
import {
  CvEditFieldParams,
  CVEditFieldStore,
  CVEFieldDefFactory,
  cveFieldStore,
  OnRenderCvEditComponent,
} from "./CVEditBaseField";

export interface DefaultCvEditDateFieldDefParams extends CvEditFieldParams {
  getData: () => GetDetailsForCandidateResponse;
}

export interface CVEditDateFieldStore extends CVEditFieldStore {
  value: Date;
}

export const defaultCvEditDateFieldDef: CVEFieldDefFactory<
  CVEditDateFieldStore,
  DefaultCvEditDateFieldDefParams
> = (args) => {
  const {
    fieldName,
    cvEditContext,
    userInfo,
    getValueVersioner,
    queryClient,
    dt,
    id,
    label,
    valueValidation: _valueValidation,
    getData,
  } = args;

  const valueValidation = (value: Date) => {
    const externalError = !!_valueValidation && _valueValidation(store.value);
    if (externalError) return externalError;

    return "";
  };

  const store = cveFieldStore({
    value: null,
    get validationError(): string {
      return valueValidation(store.value);
    },
    get showValidationError(): boolean {
      return true;
    },
  });

  const valueVersioner = getValueVersioner(store, 333, true, fieldName);

  const onNewValue = valueVersioner.onNewValue(() => {
    const data = getData();
    if (!data?.ok) return;

    return {
      valueVersion: data?.valueVersion,
      getVal: () => (data[fieldName] ? parseISO(data[fieldName]) : null),
    };
  });

  dt.disposers.push(onNewValue);

  const onChange: CalendarProps["onChange"] = action((evt) => {
    const newValue = evt.target.value as Date;

    const mutate = () =>
      userInfo.fullAPI.candidateAPI.updateCandidate({
        showSentryDialogOnError: true,
        userInfo: userInfo,
        fields: [
          {
            fieldName: fieldName,
            fieldValue: newValue ? formatISO(newValue) : null,
          },
        ],
      });

    const invalidate = () =>
      invalidateQueries(queryClient, ["candidateAPI.getDetailsForCandidate"]);

    return valueVersioner.onChange({
      newValue,
      mutateBackend: mutate,
      invalidateQuery: invalidate,
    });
  });

  const defaultOnRenderComponent: OnRenderCvEditComponent = (args) => (
    <Observer
      render={() => {
        const defaultProps = args.def.defaultGetOnRenderComponentProps(args);

        return <Calendar {...defaultProps} />;
      }}
    />
  );

  const defaultGetOnRenderComponentProps = ({ def }) => ({
    name: def.id,
    inputId: def.id,
    value: def.store.value,
    onChange,
    className: concatClassnames([
      !!store.visibleValidationError && "p-invalid",
    ]),
    touchUI: isTouchCapable(),
  });

  const L = getLocStore(userInfo, LocContext.CV);

  const getLabel = () => L.id(`cvEditFieldLabel[${id}]`).loc(label);

  return {
    ...args,
    hasUnsavedChanges: () => false,
    onChange,
    onNewValue,
    store,
    getLabel,
    defaultOnRenderComponent,
    defaultGetOnRenderComponentProps,
  };
};
